import React, { useMemo } from 'react';
import Currency from 'react-currency-formatter';
import makeStyles from '@mui/styles/makeStyles';

import { useParams } from 'react-router';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCard from '@coremat/CmtCard';
import {
  Box,
  CircularProgress,
  Divider,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useProjectToolings, useProjectWalls, useProjet, useProjetDealerCopy } from 'services/Data/ProjectServices';
import CmtGridView from '@coremat/CmtGridView';
import { Face, Home, LocationCity, MarkunreadMailbox, Phone } from '@mui/icons-material';
import { translate } from '@acnb/CustomComponents/DropdownItem/Translate';
import { useDownloadFile } from 'services/Data/UtilityServices';
import { HOST } from 'config/config';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { MoneyFlag } from '@acnb/CustomComponents/MoneyAdornment';
import { useDealer } from 'services/Data/DealerServices';
import { arrayUnique, flattenMap } from 'services/Utilities/Utilities';
import InventoryItemCategoryEnum, { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import ProfilePreviousProjectInventoryItemCategoryRow from './ProfilePreviousProjectInventoryItemCategoryRow';
import DealerCopyProjectInventoryAdjustments from 'Components/DealerCopy/DealerCopyProjectInventoryAdjustments';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import { ProjectInventoryItemAdjustmentTypeEnumValue } from 'enums/ProjectInventoryItemAdjustmentTypeEnum';
import { ProjectStatusEnumValue } from 'enums/ProjectStatusEnum';

const useStyles = makeStyles(theme => ({
  iconBigger: {
    fontSize: '20px',
  },
}));

const ProfilePreviousProject = ({ projectIdFromParent }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { projectId: projectIdFromUrl } = useParams();

  const projectId = useMemo(() => projectIdFromUrl || projectIdFromParent, [projectIdFromParent, projectIdFromUrl]);

  const { labelReference } = useLanguageReference();

  const { data: dealer } = useDealer();
  const { data: project } = useProjet(projectId);
  const { data: dealerCopy } = useProjetDealerCopy(projectId);
  const { data: projectWalls } = useProjectWalls(projectId);
  const { data: projectToolings } = useProjectToolings(projectId);

  const { mutate: downloadDealerCopyPdf, isPending: isPdfDownloading } = useDownloadFile(
    `${HOST}/api/dealer-portal/projects/${projectId}/dealer-copy/pdf`,
  );

  const flatProjectInventoryItems = useMemo(() => flattenMap(dealerCopy), [dealerCopy]);

  const validFlattenProjectInventoryItems = useMemo(
    () => flatProjectInventoryItems.filter(it => it.quantity > 0 && !it.relatedItemKey),
    [flatProjectInventoryItems],
  );

  const flatInventoryItemCategories = useMemo(() => {
    const categoryFromItems = arrayUnique(validFlattenProjectInventoryItems?.map(pii => pii.inventoryItemCategory) || []);

    if (
      validFlattenProjectInventoryItems.some(
        pii =>
          pii.tooling_Id &&
          categoryFromItems.every(c => c !== InventoryItemCategoryEnumValue.WallMountedSpecialDisplayAndTooling),
      )
    )
      categoryFromItems.push(InventoryItemCategoryEnumValue.WallMountedSpecialDisplayAndTooling);

    return categoryFromItems;
  }, [validFlattenProjectInventoryItems]);

  let contactData = useMemo(
    () => [
      { title: t('dealerContact'), desc: project?.contactName, icon: <Face /> },
      { title: t('dealerContactCell'), desc: project?.contactCell, icon: <Phone /> },
      { title: t('brpContact'), desc: project?.userFullName, icon: <Face /> },
      { title: t('shippingAddress'), desc: project?.shippingAddress ?? 'N/A', icon: <Home /> },
      { title: t('shippingCity'), desc: project?.shippingCity ?? 'N/A', icon: <LocationCity /> },
      { title: t('shippingPostalCode'), desc: project?.shippingPostalCode ?? 'N/A', icon: <MarkunreadMailbox /> },
      {
        title: t('shippingProvince'),
        desc: translate({ label: project?.shippingRegion?.label, t }) ?? 'N/A',
        icon: <MarkunreadMailbox />,
      },
    ],
    [
      project?.contactCell,
      project?.contactName,
      project?.shippingAddress,
      project?.shippingCity,
      project?.shippingPostalCode,
      project?.shippingRegion?.label,
      project?.userFullName,
      t,
    ],
  );

  const totalCol = 7;

  const dealerSoldWithOutAdjustmentPrice = validFlattenProjectInventoryItems?.reduce((acc, projectInventoryItem) => {
    return acc + projectInventoryItem?.projectDealerTotalPrice;
  }, 0);
  const dealerSoldPrice = dealerSoldWithOutAdjustmentPrice - project?.adjustmentAmount;

  const totalQuantity = validFlattenProjectInventoryItems?.reduce((acc, projectInventoryItem) => {
    return acc + projectInventoryItem.quantity;
  }, 0);

  return (
    <Box mb={6}>
      <CmtCard>
        <CmtCardHeader
          style={{ minHeight: '60px' }}
          separator={{
            color: theme.palette.borderColor.dark,
          }}
          title={`${project?.projectNumber || ''} - ${project?.dealer?.customerName || ''}`}>
          {project?.status >= ProjectStatusEnumValue.Signed && (
            <ThemedTooltip title={t('download_as_pdf')} placement="top">
              <Fab
                onClick={() => {
                  downloadDealerCopyPdf();
                }}
                style={{ float: 'right' }}
                size="small"
                color="primary"
                disabled={isPdfDownloading}>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                  {isPdfDownloading && <CircularProgress color="white" size="30px" style={{ position: 'absolute' }} />}
                  <i className={`fas fa-file-pdf ${classes.iconBigger}`} />
                </Box>
              </Fab>
            </ThemedTooltip>
          )}
        </CmtCardHeader>
        <CmtCardContent>
          <Divider sx={{ my: 4 }} />
          <CmtGridView
            itemPadding={24}
            responsive={{
              xs: 1,
              sm: 2,
              md: 3,
              lg: 3,
              xl: 3,
            }}
            data={contactData}
            renderRow={(item, index) => (
              <Box display="flex" alignItems="center" key={index}>
                {item.icon}
                <Box ml={3}>
                  <Box fontSize={12} color="text.secondary">
                    {item.title}
                  </Box>
                  <Box>{item.desc ? item.desc : item.list}</Box>
                </Box>
              </Box>
            )}
          />

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4">{t('items')}</Typography>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('image_info')}</TableCell>
                  <TableCell>{t('sku')}</TableCell>
                  <TableCell>{t('description')}</TableCell>
                  <TableCell align="center">{t('calculated_quantity')}</TableCell>
                  <TableCell style={{ textAlign: 'center' }} width={'14%'}>
                    <div>
                      {t('unitPrice')}
                      <MoneyFlag currencyCode={dealer?.currencyName} />
                    </div>
                  </TableCell>
                  <TableCell align="center">{t('total')}</TableCell>
                  <TableCell>{t('package_price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flatInventoryItemCategories &&
                  flatInventoryItemCategories?.length > 0 &&
                  flatInventoryItemCategories?.map(categoryEnum => {
                    let projectInventoryItems = validFlattenProjectInventoryItems.filter(
                      ii =>
                        ii.quantity > 0 &&
                        !ii.relatedItemKey &&
                        ((categoryEnum === InventoryItemCategoryEnumValue.MerchandisingDisplay && ii.projectWall_Id) ||
                          (categoryEnum === InventoryItemCategoryEnumValue.WallMountedSpecialDisplayAndTooling &&
                            ii.tooling_Id) ||
                          (categoryEnum !== InventoryItemCategoryEnumValue.MerchandisingDisplay &&
                            categoryEnum !== InventoryItemCategoryEnumValue.WallMountedSpecialDisplayAndTooling &&
                            ii.inventoryItemCategory === categoryEnum &&
                            !ii.projectWall_Id &&
                            !ii.tooling_Id)),
                    );

                    if (projectInventoryItems.length <= 0) {
                      return null;
                    }

                    let category = InventoryItemCategoryEnum.find(ii => ii.value === Number(categoryEnum));
                    let categoryLabel = category?.[labelReference];

                    return (
                      <ProfilePreviousProjectInventoryItemCategoryRow
                        key={categoryEnum}
                        category={category}
                        categoryLabel={categoryLabel}
                        ceilingId={project.ceiling_Id}
                        projectInventoryItems={projectInventoryItems}
                        totalCol={totalCol}
                        projectToolings={projectToolings}
                        projectWalls={projectWalls}
                      />
                    );
                  })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={totalCol}></TableCell>
                </TableRow>
                {!!project?.adjustmentAmount && (
                  <TableRow>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell>{project.adjustment}</TableCell>
                    <TableCell align="center">
                      <Currency quantity={'-' + project.adjustmentAmount} currency={dealer?.currencyName} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell align="center">{totalQuantity}</TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right">
                    <Currency quantity={dealerSoldPrice} currency={dealer?.currencyName} />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 4 }} />
          <Typography variant="h4">{t('additional_items')}</Typography>
          {project && (
            <DealerCopyProjectInventoryAdjustments
              project={project || { id: projectId }}
              totalCol={totalCol + 2}
              currencyName={dealer?.currencyName}
              cardIcon={<ApplicationIcons.AdditionalItems />}
              cardTitle={t('additional_items')}
              adjustmentType={ProjectInventoryItemAdjustmentTypeEnumValue.AdditionalItem}
            />
          )}

          <Divider sx={{ my: 4 }} />
          <Typography variant="h4">{t('dealerInventoryItems')}</Typography>
          {project && (
            <DealerCopyProjectInventoryAdjustments
              project={project || { id: projectId }}
              totalCol={totalCol + 2}
              currencyName={dealer?.currencyName}
              cardIcon={<ApplicationIcons.DealerInventory />}
              cardTitle={t('dealerInventory')}
              adjustmentType={ProjectInventoryItemAdjustmentTypeEnumValue.DealerInventory}
            />
          )}
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default ProfilePreviousProject;
